import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormTypes } from '../../../hooks/useApi';
import { useI18n } from '../../context/I18nContext';

/**
 * Form Selection Page
 * Displays all available form types for users to choose from
 */
const FormSelector = () => {
    const navigate = useNavigate();
    const { t, getText, language } = useI18n(['common', 'forms']);
    const [selectedCategory, setSelectedCategory] = useState('all');

    // React Query hook
    const { 
        data: formTypesResult, 
        isLoading: loading, 
        error: queryError 
    } = useFormTypes(language);

    const formTypes = formTypesResult?.data || [];
    const error = queryError?.message;

    // Get all categories
    const categories = ['all', ...new Set(formTypes.map(f => f.category))];

    // Filter forms by selected category
    const filteredForms = selectedCategory === 'all'
        ? formTypes
        : formTypes.filter(f => f.category === selectedCategory);

    const handleSelectForm = (formId) => {
        navigate(`/forms/new/${formId}`);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-6xl">
            {/* Page Title */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">{t('selectForm', { ns: 'forms' })}</h1>
                <p className="mt-2 text-gray-600">{t('selectFormDesc', { ns: 'forms' })}</p>
            </div>

            {/* Category Filter */}
            <div className="mb-6 flex flex-wrap gap-2">
                {categories.map(category => (
                    <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            selectedCategory === category
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {category === 'all' ? t('showAll', { ns: 'forms' }) : category}
                    </button>
                ))}
            </div>

            {/* Form Card List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredForms.map(form => (
                    <div
                        key={form.id}
                        onClick={() => handleSelectForm(form.id)}
                        className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow cursor-pointer border border-gray-200 overflow-hidden"
                    >
                        <div className="p-6">
                            {/* Icon */}
                            <div className="text-4xl mb-4">{form.icon}</div>

                            {/* Title - 使用 getText 處理多語言物件 */}
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                {getText(form.name)}
                            </h3>

                            {/* Description - 使用 getText 處理多語言物件 */}
                            <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                                {getText(form.description)}
                            </p>

                            {/* Category Tag */}
                            <div className="flex items-center justify-between">
                                <span className="inline-block px-3 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                    {form.category}
                                </span>
                                <span className="text-xs text-gray-500">v{form.version}</span>
                            </div>
                        </div>

                        {/* Bottom Action Area */}
                        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                            <button className="text-blue-600 font-medium text-sm hover:text-blue-800">
                                {t('startFilling', { ns: 'forms' })} →
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Empty State */}
            {filteredForms.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-gray-400 text-6xl mb-4">📝</div>
                    <p className="text-gray-500">{t('noFormsInCategory', { ns: 'forms' })}</p>
                </div>
            )}
        </div>
    );
};

export default FormSelector;
